import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import type { Audiobook, AudiobookPage } from '@/hooks/use-audiobooks';

interface AudiobookSectionProps {
  audiobooks: Audiobook[];
  currentPage: AudiobookPage | null;
  isPlaying: boolean;
  selectedAudiobookId?: number | null;
  onSelectAudiobook: (audiobook: Audiobook) => void;
}

export function AudiobookSection({
  audiobooks,
  currentPage,
  isPlaying,
  selectedAudiobookId,
  onSelectAudiobook,
}: AudiobookSectionProps) {
  if (!audiobooks || audiobooks.length === 0) return null;

  return (
    <div className="px-5 pt-6 pb-4">
      <h2 className="text-white/60 font-semibold text-xs uppercase tracking-wider mb-3">
        Audiobooks
      </h2>
      
      <div className="space-y-0.5">
        {audiobooks.map((audiobook) => {
          const isPlayingThisBook = currentPage?.bookId === audiobook.id;
          const isSelected = selectedAudiobookId === audiobook.id;
          
          return (
            <motion.button
              key={audiobook.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectAudiobook(audiobook)}
              className={cn(
                "w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors",
                isSelected ? "bg-white/10" : "active:bg-white/5"
              )}
            >
              {/* Cover with playing indicator */}
              <div className="relative w-11 h-11 shrink-0 rounded-md overflow-hidden">
                <img 
                  src={audiobook.coverUrl} 
                  alt={audiobook.title}
                  className="w-full h-full object-cover"
                />
                {isPlayingThisBook && isPlaying && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="flex items-end gap-[2px] h-3">
                      <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '50%' }} />
                      <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.15s' }} />
                      <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '70%', animationDelay: '0.3s' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 text-left">
                <p className={cn(
                  "font-medium text-sm truncate",
                  isPlayingThisBook ? "text-primary" : "text-white"
                )}>
                  {audiobook.title}
                </p>
                <p className="text-white/40 text-xs truncate">
                  {audiobook.author}
                </p>
              </div>

              {/* Pages count */}
              <span className="text-white/30 text-xs shrink-0">
                {audiobook.totalPages} pages
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
